import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Checkbox, InputBase, CircularProgress } from "@mui/material";
import { FixedSizeList } from "react-window";
import { FaSearch } from "react-icons/fa";
import CheckIcon from "@mui/icons-material/Check";
import axiosInstance from "../../api/axiosInstance";

const FILTER_CHECKBOX_SIZE = 17;
const FILTER_CHECK_ICON_SIZE = 13;

const filterCheckboxIcon = (
  <Box
    sx={{
      width: FILTER_CHECKBOX_SIZE,
      height: FILTER_CHECKBOX_SIZE,
      borderRadius: "5px",
      border: "1.5px solid #cbd5e1",
      bgcolor: "#fff",
      flexShrink: 0,
    }}
  />
);

const filterCheckboxCheckedIcon = (
  <Box
    sx={{
      width: FILTER_CHECKBOX_SIZE,
      height: FILTER_CHECKBOX_SIZE,
      borderRadius: "5px",
      bgcolor: "#2563eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <CheckIcon sx={{ fontSize: FILTER_CHECK_ICON_SIZE, color: "#fff" }} />
  </Box>
);

/**
 * Renders a single row in the virtualised list.
 * Defined OUTSIDE the component so its reference never changes between renders —
 * react-window uses the component reference as a key and would remount every row
 * if this were defined inside the component body.
 */
const MemberRow = ({ index, style, data }) => {
  const { items, selectedIds, onToggle, loading, hasMore } = data;

  // Last slot = loading / end-of-list indicator
  if (index === items.length) {
    return (
      <Box
        style={style}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 1 }}
      >
        {loading && <CircularProgress size={20} />}
        {!loading && !hasMore && items.length > 0 && (
          <Typography sx={{ color: "#9ca3af", fontSize: "0.85rem" }}>End of list</Typography>
        )}
      </Box>
    );
  }

  const emp = items[index];
  if (!emp) return null;

  const idStr = String(emp.userId || emp.id);
  const checked = selectedIds.includes("all") || selectedIds.includes(idStr);
  const fn = emp.first_name || emp.FirstName || emp.firstName || "";
  const ln = emp.last_name || emp.LastName || emp.lastName || "";
  const label = `${fn} ${ln}`.trim() || emp.email || emp.Email || "";

  return (
    <Box
      style={style}
      onClick={() => onToggle(idStr)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.8,
        px: 1.6,
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
    >
      <Checkbox
        checked={checked}
        icon={filterCheckboxIcon}
        checkedIcon={filterCheckboxCheckedIcon}
        disableRipple
        sx={{ p: 0.5, "&:hover": { bgcolor: "transparent" } }}
      />
      <Typography
        sx={{
          flex: 1,
          fontSize: "0.95rem",
          color: "#1f2937",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const TeamMembersFilterPane = ({ selectedIds = [], onToggle, onSelectAll, type = "me" }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const listRef = useRef(null);
  // Tracks the search value that is currently reflected in `items`.
  const committedSearch = useRef("");
  // Mirror of hasMore/loading as refs so handleItemsRendered can read
  // them without being a dependency (avoids stale closure issues).
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;
    let debounceTimer = null;

    const doFetch = async (q, pg) => {
      if (isCancelled) return;
      loadingRef.current = true;
      setLoading(true);
      try {
        const res = await axiosInstance.get("/api/auth/team-members", {
          params: { search: q, page: pg, limit: 20, type },
        });
        if (isCancelled) return;

        const newItems = res.data?.data?.teamMembers || [];
        const pagination = res.data?.data?.pagination || {};
        const more = pg < (pagination.totalPages || 1);

        setItems((prev) => (pg === 1 ? newItems : [...prev, ...newItems]));
        hasMoreRef.current = more;
        setHasMore(more);
      } catch (e) {
        if (!isCancelled) console.error("Failed to load team members:", e);
      } finally {
        if (!isCancelled) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    };

    const searchChanged = search !== committedSearch.current;

    if (searchChanged) {
      // User typed → debounce 300ms, then reset list and fetch page 1
      debounceTimer = setTimeout(() => {
        if (isCancelled) return;
        committedSearch.current = search;
        hasMoreRef.current = true;
        setPage(1);
        setItems([]);
        setHasMore(true);
        doFetch(search, 1);
      }, 300);
    } else {
      // Mount or page increment → fetch immediately
      doFetch(search, page);
    }

    return () => {
      isCancelled = true;
      if (debounceTimer) clearTimeout(debounceTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, type]);

  // Trigger load-more when user scrolls near the bottom
  const handleItemsRendered = useCallback(({ visibleStopIndex, visibleStartIndex }) => {
    setItems((currentItems) => {
      if (
        currentItems.length > 0 &&
        visibleStopIndex >= currentItems.length - 2 &&
        hasMoreRef.current &&
        !loadingRef.current
      ) {
        setPage((p) => p + 1);
      }
      return currentItems; // no actual state change, just reading
    });
  }, []);

  const itemCount = items.length + (hasMore || loading ? 1 : 0);
  const itemData = { items, selectedIds, onToggle, loading, hasMore };

  const handleSelectAllClick = () => {
    if (onSelectAll) {
      onSelectAll(["all"]);
    }
  };

  const allVisibleSelected = selectedIds.includes("all");

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* Search box */}
      <Box sx={{ p: 1.6, pb: 0.8 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.8,
            border: "1px solid #2563eb",
            borderRadius: "8px",
            px: 1.2,
            py: "6px",
          }}
        >
          <FaSearch color="#9ca3af" size={12} />
          <InputBase
            fullWidth
            autoFocus
            placeholder="Search team members…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: "0.95rem", "& input": { py: 0 } }}
          />
        </Box>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {loading && items.length === 0 ? (
          // Initial load spinner — shown while first page is in-flight
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : !loading && items.length === 0 ? (
          <Typography sx={{ color: "#9ca3af", fontSize: "0.9rem", p: 1.6 }}>
            No team members found.
          </Typography>
        ) : (
          <>
            <Box
              onClick={handleSelectAllClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                py: 0.6,
                px: 1.6,
                cursor: "pointer",
                borderBottom: "1px solid #f1f5f9",
                mb: 1,
                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
              }}
            >
              <Checkbox
                checked={allVisibleSelected}
                icon={filterCheckboxIcon}
                checkedIcon={filterCheckboxCheckedIcon}
                disableRipple
                sx={{ p: 0.5, "&:hover": { bgcolor: "transparent" } }}
              />
              <Typography sx={{ flex: 1, fontSize: "0.95rem", color: "#1f2937", fontWeight: 600 }}>
                Select All
              </Typography>
            </Box>
            <FixedSizeList
              ref={listRef}
              height={220} // Slightly reduced to fit Select All
              width="100%"
              itemSize={36}
              itemCount={itemCount}
              itemData={itemData}
              onItemsRendered={handleItemsRendered}
              overscanCount={5}
            >
              {MemberRow}
            </FixedSizeList>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TeamMembersFilterPane;
